import { round } from './round';
import { getOrderEffectiveAmount } from './orderEffectiveAmount';

// When editing a booked (non-draft) invoice, the order's billed figures already
// include this invoice — so "already billed" and the open volume would be
// double-counted. Remove the edited invoice's own contribution so the numbers
// reflect what was billed by the *previous* invoices only. Draft invoices are
// not yet counted against the order, so no adjustment is needed.
export function excludeInvoiceFromOrder(order, invoice) {
  if (!order) return order;
  const isCounted = Boolean(invoice?.id) && !invoice.isDraft;
  if (!isCounted) return order;

  // Netto money this invoice bills per order position.
  const billedPerOrderPosition = {};
  (invoice.positions || []).forEach((pos) => {
    if (!pos.isOrderPosition || pos.orderPositionId == null) return;
    const money = round(Number(pos.amount) * Number(pos.price)) || 0;
    billedPerOrderPosition[pos.orderPositionId] =
      (billedPerOrderPosition[pos.orderPositionId] || 0) + money;
  });

  const positions = (order.positions || []).map((op) => {
    const ownBilled = billedPerOrderPosition[op.id];
    if (!ownBilled) return op;
    return { ...op, billedTotal: round((Number(op.billedTotal) || 0) - ownBilled) };
  });

  const invoices = (order.invoices || []).filter(
    (inv) => inv.id !== invoice.id,
  );

  return { ...order, positions, invoices };
}

// Maps a raw order (from GET /orders/:id) into the flattened order fields the
// order-based invoice stepper and its live preview rely on (orderPositions,
// previousInvoices, orderPrice, ...). Kept in one place so the initial edit
// state (NewProgressBilling) and the "Invoices" step (StepOrderInvoices) stay
// in sync — the preview reads these fields regardless of the active step.
export function buildOrderData(order, dataCollection = {}) {
  const orderData = {
    previousInvoices: order.invoices,
    orderTitle: order.title,
    orderPrice: round(order.volume),
    orderNr: order.nr,
    orderPositions: order.positions,
    orderStartDate: order.startDate,
    orderTaxAmount: order.taxVolume,
    orderDiscountPercent:
      order.discountPercent || dataCollection.orderDiscountPercent || 0,
    orderTaxRate: dataCollection?.templateMetaData?.taxRate || 19,
    isFlat: order.hasFlatInvoice || false,
    ...(order.project && { project: order.project }),
  };

  // Sum of the already-invoiced (order-effective) amount of previous invoices.
  const useIneffective = dataCollection?.templateMetaData?.useOrderIneffective;
  let previousInvoicesSum = 0;
  order.invoices?.forEach((inv) => {
    previousInvoicesSum += getOrderEffectiveAmount(inv, useIneffective).netto;
  });

  return { ...orderData, previousInvoicesSum };
}
